import {
  useState,
  useEffect,
  useRef,
  useMemo,
  useContext,
  useCallback
} from "react";
import { AgGridReact } from "ag-grid-react"; // the AG Grid React Component
import { Button, message } from "antd";

import { ColDef } from "ag-grid-enterprise";
import {
  projectDateCellRenderer,
  IdLinkProjectSumbittalListComponent,
  projectOverviewCellRenderer,
  truncatedTextCellRenderer,
  dummyColumnDef
} from "components/cell-renders";
import { useAcceptProjectInvite } from "hooks/project";
import {
  RoleSubscriptionEnum,
  SubscriptionContext
} from "context/SubscriptionProvider";
import { useHistory } from "react-router";
import { GridLoadingIndicator } from "components/widgets";
import { useQuery } from "@apollo/client";
import { QUERY_GET_SUBSCRIPTION_DETAILS } from "services/graphQL/queries";
import { DateUtils } from "utils/dateutils";
import DeleteProjectComponent from "components/delete-project";
import {
  FilterChipComponent,
  FilterChipComponentRef
} from "entity-app/shared-components/log-render-components/filter-chip-component";
import CiqLogPageHeader, {
  calculateLogPageStats
} from "components/ciq-log-page-header";
import CiqAgSearch from "components/ciq-ag-search";
import { getAccessToken, getProjectListV2 } from "services/auth";
import useCheckVersionUpdates from "hooks/check-version-updates";

import CustomDateSetFilter from "entity-app/custom-filters/custom-date-filter";
import { AcceptanceStatus, ErrorMessages } from "../../constants";
import { TProject } from "./models";

function ProjectLinkWithInviteRenderer(props: any) {
  const { data, onInviteAccepted } = props;
  const { acceptProjectInvite, acceptanceState } = useAcceptProjectInvite();

  const onAccept = () => {
    acceptProjectInvite(data.id);
  };

  useEffect(() => {
    if (acceptanceState.isCompleted) {
      onInviteAccepted();
    }
  }, [acceptanceState, onInviteAccepted]);

  return (
    <div className="w-full flex items-center justify-between">
      <div className="text-ellipsis overflow-hidden">
        <IdLinkProjectSumbittalListComponent {...props} />
      </div>
      {data.status_id === AcceptanceStatus.INVITED && (
        <div>
          <Button
            onClick={() => {
              onAccept();
            }}
            loading={acceptanceState.isLoading}
            disabled={acceptanceState.isLoading}
            data-testid="projectaggridlistcomponent-button-accept-invite"
          >
            Accept Project Invite
          </Button>
        </div>
      )}
    </div>
  );
}

function ProjectAgGridListComponent() {
  const history = useHistory();
  const subscriptionToken: any = getAccessToken();
  const { subscriptionRole, subscriptionId } = useContext(SubscriptionContext);

  useCheckVersionUpdates({
    autoCheckInIntervals: false
  });

  const gridRef = useRef<AgGridReact>(null);
  const [isGridReady, setGridReady] = useState(false);
  const [projectListData, setProjectListData] = useState<any>(null);

  const [statsData, setStatsData] = useState<{
    displayCount: number;
    filterName: undefined | string;
  }>();
  const filterChipRef = useRef<FilterChipComponentRef>(null);

  const { data: subscriptionDetail } = useQuery(
    QUERY_GET_SUBSCRIPTION_DETAILS,
    {
      variables: {
        id: subscriptionId
      }
    }
  );

  const fetchProjectListData = useCallback(async () => {
    try {
      const res = await getProjectListV2(subscriptionToken);

      if (res && res.success && Array.isArray(res.data?.data)) {
        setProjectListData([...res.data.data]);
      } else {
        message.error("An error occurred while fetching projects.");
        if (res && res.error) {
          console.error("Project list fetch error:", res.error);
        }
      }
    } catch (error) {
      message.error("An unexpected error occurred.");
      console.error("Project list fetch exception:", error);
    }
  }, [subscriptionToken]);

  useEffect(() => {
    if (subscriptionId && subscriptionToken) {
      fetchProjectListData();
    }
    return () => {};
  }, [fetchProjectListData, subscriptionId, subscriptionToken]);

  useEffect(() => {
    gridRef.current?.api?.setGridOption("loading", !projectListData);
  }, [projectListData]);

  useEffect(() => {
    function handleResize() {
      if (isGridReady && gridRef && gridRef.current) {
        gridRef?.current?.api?.sizeColumnsToFit();
      }
    }
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, [isGridReady]);

  const onInviteAccepted = useCallback(() => {
    fetchProjectListData();
  }, [fetchProjectListData]);

  const onProjectDeleted = useCallback(() => {
    fetchProjectListData();
  }, [fetchProjectListData]);

  const columnDefs = useMemo<ColDef[]>(() => {
    return [
      dummyColumnDef,
      {
        colId: "name",
        field: "name",
        headerName: "Project Name",
        headerTooltip: "Project Name",
        menuTabs: [],
        width: 280,
        minWidth: 120,
        maxWidth: 500,
        cellRenderer: ProjectLinkWithInviteRenderer,
        cellRendererParams: {
          onInviteAccepted
        },
        comparator: (valueA: any, valueB: any) => {
          return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
        },
        sort: "asc"
      },
      {
        colId: "number",
        field: "number",
        headerName: "Project Number",
        headerTooltip: "Project Number",
        menuTabs: [],
        width: 160,
        minWidth: 70,
        maxWidth: 200,
        cellRenderer: truncatedTextCellRenderer
      },
      {
        colId: "overview",
        headerName: "Record Counts",
        headerTooltip: "Record Counts",
        menuTabs: [],
        width: 230,
        minWidth: 230,
        maxWidth: 300,
        sortable: false,
        filter: false,
        cellRenderer: projectOverviewCellRenderer
      },
      {
        colId: "details",
        field: "details",
        headerName: "Details",
        headerTooltip: "Project Details",
        menuTabs: [],
        width: 250,
        minWidth: 120,
        maxWidth: 500,
        comparator: (valueA: any, valueB: any) => {
          return valueA?.toLowerCase().localeCompare(valueB?.toLowerCase());
        },
        cellRenderer: truncatedTextCellRenderer
      },
      {
        colId: "address",
        field: "address",
        headerName: "Address",
        headerTooltip: "Address",
        menuTabs: [],
        comparator: (valueA: any, valueB: any) => {
          return valueA?.toLowerCase().localeCompare(valueB?.toLowerCase());
        },
        cellRenderer: truncatedTextCellRenderer
      },
      {
        colId: "start_date",
        field: "start_date",
        headerName: "Start Date",
        headerTooltip: "Start Date",
        minWidth: 120,
        menuTabs: ["filterMenuTab"],
        cellRenderer: projectDateCellRenderer,
        cellRendererParams: { field: "start_date" },
        filter: CustomDateSetFilter,
        getQuickFilterText(params: { data: TProject }) {
          return params.data.start_date
            ? DateUtils.format(params.data.start_date)
            : "";
        },
        comparator: (value1: string, value2: string): number => {
          const date1 = new Date(value1);
          const date2 = new Date(value2);
          if (date1 === date2) {
            return 0;
          }
          return date1 > date2 ? 1 : -1;
        }
      },
      {
        colId: "end_date",
        field: "end_date",
        headerName: "End Date",
        headerTooltip: "End Date",
        minWidth: 120,
        menuTabs: ["filterMenuTab"],
        cellRenderer: projectDateCellRenderer,
        cellRendererParams: { field: "end_date" },
        filter: CustomDateSetFilter,
        getQuickFilterText(params: { data: TProject }) {
          return params.data.end_date
            ? DateUtils.format(params.data.end_date)
            : "";
        },
        comparator: (value1: string, value2: string): number => {
          const date1 = new Date(value1);
          const date2 = new Date(value2);
          if (date1 === date2) {
            return 0;
          }
          return date1 > date2 ? 1 : -1;
        }
      },
      {
        colId: "actions",
        field: "actions",
        headerName: "Actions",
        headerTooltip: "Actions",
        pinned: "right",
        menuTabs: [],
        minWidth: 80,
        maxWidth: 90,
        cellRenderer: DeleteProjectComponent,
        cellRendererParams: {
          onProjectDeleted
        },
        hide: subscriptionRole !== RoleSubscriptionEnum.subscription_admin
      }
    ];
  }, [onInviteAccepted, onProjectDeleted, subscriptionRole]);

  const defaultColDef: {} = useMemo<ColDef>(() => {
    return {
      sortable: true,
      resizable: true,
      editable: false
    };
  }, []);

  const onFiltersApplied = () => {
    filterChipRef.current?.onFilterApplied();
    setStatsData(
      calculateLogPageStats({
        gridRef
      })
    );
  };

  const renderProjects = () => {
    if (projectListData && projectListData?.length === 0) {
      return (
        <div className="h-[300px] flex items-center justify-center">
          <span>
            Start by creating projects or contact your Admin to invite you to a
            project
          </span>
        </div>
      );
    }

    return (
      <AgGridReact<any>
        ref={gridRef}
        onGridReady={() => {
          setGridReady(true);
          gridRef?.current?.api?.sizeColumnsToFit();
        }}
        onFirstDataRendered={() => {
          gridRef?.current?.api?.sizeColumnsToFit();
        }}
        rowData={projectListData}
        columnDefs={columnDefs} // Column Defs for Columns
        defaultColDef={defaultColDef} // Default Column Properties
        suppressContextMenu
        onFilterChanged={onFiltersApplied}
        loadingOverlayComponent={GridLoadingIndicator}
        className="ag-theme-alpine"
        suppressScrollOnNewData
        suppressDragLeaveHidesColumns
        tooltipShowDelay={0}
        tooltipHideDelay={2000}
        tooltipShowMode="whenTruncated"
        selectionColumnDef={{
          width: 40
        }}
        context={{
          isProjectList: true
        }}
      />
    );
  };

  const headerItems = [
    <CiqAgSearch
      key="SubscriptionProjects_search"
      gridRef={gridRef}
      placeholder="Search Projects"
    />,
    <FilterChipComponent
      key="SubscriptionProjects_FilterChip"
      gridRef={gridRef}
      ref={filterChipRef}
    />,
    <Button
      key="SubscriptionProjects_AddNew"
      type="primary"
      title={
        !subscriptionDetail?.subscription_by_pk?.paid ||
        subscriptionRole <= RoleSubscriptionEnum.subscription_viewer
          ? ErrorMessages.PermissionNotGranted
          : "Add Project"
      }
      onClick={() => history.push("/new-project")}
      disabled={
        !subscriptionDetail?.subscription_by_pk?.paid ||
        subscriptionRole <= RoleSubscriptionEnum.subscription_viewer
      }
      data-testid="projectaggridlistcomponent-button-add-project"
    >
      Add Project
    </Button>
  ];

  return (
    <>
      <CiqLogPageHeader
        {...{
          entityName: "Projects",
          titleParam: {
            title: "Projects",
            totalCount: projectListData?.length,
            filterStats: statsData
          },
          gridRef,
          items: headerItems
        }}
      />

      <div className="grow">{renderProjects()}</div>
    </>
  );
}

export default ProjectAgGridListComponent;
